/**
 * The left rail: project switcher at the top, destinations in the middle,
 * account and the Simple and Advanced switch at the bottom.
 *
 * The switcher renders its resting state (avatar, name, status, chevron) and
 * does not open yet. The 320px popover, the fuzzy find field and the rule that
 * switching project preserves the current sub route land with the switcher
 * pass.
 *
 * The status was a bare colored dot, which the verify gate flagged and was
 * right to: color was the only channel carrying it, so in greyscale, or to
 * roughly one man in twelve, the rail said nothing about the project it is
 * scoped to. It is a status pill at `sm` now. Screen spec 4.1 is explicit that
 * the word is never dropped at any size, including in the icon rail: color
 * plus glyph plus word, always. The project name yields the space, because a
 * truncated name with a full title attribute is a smaller loss than a state
 * nobody can read.
 */

import { Link } from 'react-router';
import { t } from '../../lib/i18n/t';
import { Wordmark } from '../brand/Wordmark';
import { AccountMenu } from './AccountMenu';
import { ProjectNav } from './ProjectNav';
import { EnvironmentSwitch } from './EnvironmentSwitch';
import { ProjectSwitcher } from './ProjectSwitcher';

interface SidebarProps {
  slug: string | null;
}

export function Sidebar({ slug }: SidebarProps) {
  return (
    /* data-tour is the only thing the first run tour needs from the rail. The
       tour points at the whole rail rather than at one item in it, because
       what a first time person has to learn here is not a destination, it is
       that the rail IS the project: open one and this list becomes its parts.
       Under 900px the rail is display:none and the tour reads its zero box as
       "not on this screen", so the step renders as a card with no ring rather
       than pointing at a corner where nothing is. */
    <aside className="rail" data-tour="rail">
      <div className="rail__brand">
        <Link to="/projects" aria-label={t('All projects')}>
          <Wordmark />
        </Link>
      </div>

      <ProjectSwitcher slug={slug} />

      {/* Directly under the app it belongs to, and above its screens: the two
          environments are two readings of the same rail below. */}
      <EnvironmentSwitch slug={slug} />

      <ProjectNav slug={slug} />

      <AccountMenu />
    </aside>
  );
}
